import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import Layout from '@components/layout';
import { graphql } from 'gatsby';
import ReactPlayer from 'react-player';
import RichTextBlock from '@repo/ui-components/dept/organisms/Blocks/RichTextBlock';
import DeptGrid from '@repo/ui-components/dept/design-system/DeptGrid';
import {
  GridItem,
  Container,
  Text,
  Flex,
  Box,
  AspectRatio,
} from '@chakra-ui/react';
import { Prose } from '@nikolovlazar/chakra-ui-prose';

import ProductCard from '@repo/ui-components/tailwind/ProductCard';
import SimpleSlider from '@repo/ui-components/dept/molecules/SimpleSlider';
import BlogExpertBadge from '@repo/ui-components/tailwind/BlogExpertBadge';
import BlogAuthorCard from '@repo/ui-components/dept/atoms/BlogAuthorCard';
import BlogShare from '@repo/ui-components/dept/atoms/BlogShare';
import CardRowThin from '@repo/ui-components/dept/atoms/CardRowThin';
import MiscNews from '@repo/ui-components/dept/molecules/MiscNews';
import Blocks from '@repo/ui-components/dept/organisms/Blocks/Blocks';
import ConvertAB from '@repo/ui-components/tailwind/ConvertAB';
import Image from '@repo/ui-components/tailwind/Image';
import Link from '@repo/ui-components/tailwind/Link';

import createOpenGraphTags from '@repo/utils/createOpenGraphTags';
import { usePetsafeInternationalContext } from '@util/static-translations';
import { getLocalizedPagePath } from '@repo/gatsby-localization-helpers';

const isBrowser = typeof window !== 'undefined';

function ProductBox(product) {
  return (
    <Box w={['16.3rem', null, '18.3rem']}>
      {product && <ProductCard product={product} />}
    </Box>
  );
}

export default function BlogPost({ data, pageContext }) {
  const { post, sanitySiteSettings } = data;
  const { linkHreflangValues } = pageContext;

  const { tt, locale, localizeDate } = usePetsafeInternationalContext();

  if (!post) return null;

  const {
    publishedDate,
    title,
    html,
    categories,
    author,
    image,
    relatedProducts,
    relatedArticles,
    video,
    articleType,
    contentType,
  } = post;

  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  const formattedDate = localizeDate(publishedDate, options);

  return (
    <>
      <ConvertAB
        pageType="blog"
        categoryName={post?.categories?.map(c => c.title).join(',') || ''}
      />
      <Layout
        headTagData={{
          title:
            post?.SEOMetadata?.title ||
            tt(s => s.commonLabels.petsafeTitleLabel, {
              title,
            }),
          description: post?.SEOMetadata?.description || post?.description,
          canonicalUrl: post?.SEOMetadata?.canonicalUrl,
          noIndex: post?.SEOMetadata?.noIndex,
          noFollow: post?.SEOMetadata?.noFollow,
          includeBVScript: true,
          enableKlaviyo: true,
          meta: createOpenGraphTags(
            {
              title: post?.SEOMetadata?.ogTitle || post?.SEOMetadata?.title,
              description:
                post?.SEOMetadata?.ogDescription ||
                post?.SEOMetadata?.description ||
                post?.description,
              url: getLocalizedPagePath(
                post?.SEOMetadata?.ogUrl ||
                  (isBrowser && window?.location?.pathname) ||
                  '/',
                locale
              ),
              image: post?.SEOMetadata?.ogImage || post?.image,
            },
            'petsafe'
          ),
        }}
        linkHreflangValues={linkHreflangValues}
      >
        <Container pb={10} pt={[2, null, 10]}>
          <DeptGrid>
            <GridItem colSpan={{ base: 6, lg: 8 }}>
              {articleType === 'Video' && video?.url ? (
                <AspectRatio w="100%" ratio={16 / 9}>
                  <ReactPlayer
                    url={`${video?.url}`}
                    width="100%"
                    height="100%"
                    style={{
                      borderRadius: '8px',
                    }}
                  />
                </AspectRatio>
              ) : (
                <Image
                  className="mb-6 rounded-3xl md:mb-10"
                  ratio={16 / 9}
                  sanityImage={image}
                  srcSetBasisWidth={950}
                />
              )}
              <Flex
                justifyContent="space-between"
                flexDirection={{ base: 'column', xl: 'row' }}
                gap={4}
              >
                <Box display={['block', null, 'flex']}>
                  {categories?.filter(Boolean)?.map(category => (
                    <Text
                      as={Link}
                      className="font-semibold mr-2 text-muted-foreground"
                      key={category._id}
                      textStyle="bodyLarge"
                      link={`/blog/${category.slug?.current}`}
                    >
                      {category.title}
                      {' • '}
                    </Text>
                  ))}
                  {publishedDate && (
                    <Text
                      textStyle="bodyLarge"
                      fontWeight="semibold"
                      color="typography.light"
                    >
                      {formattedDate}
                    </Text>
                  )}
                </Box>
                {author?.title && author?.slug?.current && (
                  <div>
                    <BlogExpertBadge
                      author={author?.title}
                      slug={author?.slug?.current}
                    />
                  </div>
                )}
              </Flex>
              <DeptGrid>
                <GridItem colSpan={{ base: 6, lg: 8 }}>
                  <Text
                    as="h1"
                    textStyle="h2"
                    color="brand.ink"
                    fontWeight="extrabold"
                    mb={[6, null, 10]}
                    mt={[8, null, 2]}
                  >
                    {title}
                  </Text>
                </GridItem>
              </DeptGrid>
              <BlogShare image={image?.asset?.url} title={title} />
              {contentType === 'html' ? (
                <Prose
                  id="post-body"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ) : (
                <RichTextBlock block={post} />
              )}
              {author?.title && author?.slug?.current && (
                <BlogAuthorCard author={author} />
              )}
            </GridItem>
            <GridItem display={{ base: 'none', xl: 'block' }} />
            <GridItem colSpan={{ base: 6, lg: 4, xl: 3 }}>
              <MiscNews
                sidebar
                newsletter={sanitySiteSettings.newsletter}
                h={['24.125rem', null, '29.625rem']}
              />
              {relatedArticles?.length ? (
                <Box mt={14}>
                  <Text
                    textStyle="h4_5_6"
                    as="h4"
                    textAlign={['left', null, 'center']}
                    mb={6}
                  >
                    {tt(s => s.blogPostPage.relatedArticles)}
                  </Text>
                  <Box
                    overflow={['auto', null, 'visible']}
                    boxShadow={[
                      '0px 4px 56px 0px rgba(5, 37, 77, 0.15)',
                      null,
                      'none',
                    ]}
                  >
                    <Flex
                      flexDirection={['row', null, 'column']}
                      width={[relatedArticles.length * 318, null, 'auto']}
                      gap={[4, null, 6]}
                    >
                      {relatedArticles?.map(article => (
                        <CardRowThin key={article._id} post={article} />
                      ))}
                    </Flex>
                  </Box>
                </Box>
              ) : null}
            </GridItem>
          </DeptGrid>
          {relatedProducts?.length ? (
            <Box pt={20}>
              <Text textStyle="h4_5_6" as="h4" mb={10}>
                {tt(s => s.blogPostPage.relatedProducts)}
              </Text>
              <SimpleSlider items={relatedProducts} sliderItem={ProductBox} />
            </Box>
          ) : null}
        </Container>
        <Suspense fallback={null}>
          {sanitySiteSettings.postBlocks?.blocks?.length && (
            <Blocks
              blocks={sanitySiteSettings.postBlocks?.blocks}
              siteSettings={sanitySiteSettings}
            />
          )}
        </Suspense>
      </Layout>
    </>
  );
}

BlogPost.propTypes = {
  data: PropTypes.shape({
    post: PropTypes.shape({
      SEOMetadata: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        canonicalUrl: PropTypes.string,
        noIndex: PropTypes.bool,
        noFollow: PropTypes.bool,
        ogTitle: PropTypes.string,
        ogDescription: PropTypes.string,
        ogUrl: PropTypes.string,
        ogImage: PropTypes.string,
      }),
      _id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      slug: PropTypes.shape({
        current: PropTypes.string.isRequired,
      }).isRequired,
      image: PropTypes.shape({
        asset: PropTypes.shape({
          url: PropTypes.string.isRequired,
        }).isRequired,
        alt: PropTypes.string.isRequired,
      }).isRequired,
      description: PropTypes.string.isRequired,
      articleType: PropTypes.string,
      video: PropTypes.shape({
        url: PropTypes.string,
        thumbnail: PropTypes.string,
        caption: PropTypes.string,
      }),
      body: PropTypes.arrayOf(PropTypes.shape({})),
      html: PropTypes.string,
      contentType: PropTypes.string,
      publishedDate: PropTypes.string.isRequired,
      categories: PropTypes.arrayOf(
        PropTypes.shape({
          _type: PropTypes.string,
          title: PropTypes.string,
          slug: PropTypes.shape({ current: PropTypes.string.isRequired }),
        })
      ),
      author: PropTypes.shape({
        title: PropTypes.string.isRequired,
        slug: PropTypes.shape({
          current: PropTypes.string.isRequired,
        }),
      }),
      relatedProducts: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          title: PropTypes.string,
        })
      ),
      relatedArticles: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          _type: PropTypes.string,
        })
      ),
    }).isRequired,
    sanitySiteSettings: PropTypes.shape({
      newsletter: PropTypes.shape({
        newsletterTitle: PropTypes.string,
        newsletterDescription: PropTypes.string,
      }).isRequired,
      postBlocks: PropTypes.shape({
        blocks: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    }).isRequired,
  }),
  pageContext: PropTypes.shape({ linkHreflangValues: {} }).isRequired,
};

BlogPost.defaultProps = {
  data: {
    post: {
      html: null,
      categories: [],
      relatedProducts: [],
      relatedArticles: [],
    },
  },
};

// TODO: return language filter after ability to localize site settings
export const query = graphql`
  query blogPostQuery($_id: String, $locale: String) {
    sanitySiteSettings(
      siteIdentifier: { eq: "petsafe" }
      language: { eq: $locale }
    ) {
      newsletter {
        newsletterDescription
        newsletterTitle
      }
      postBlocks {
        ...DeptBlockFields
      }
      ...SiteSettingsFields
    }
    post: sanityBlogPost(_id: { eq: $_id }, siteIdentifier: { eq: "petsafe" }) {
      _id
      title
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            _id
            url
          }
        }
      }
      slug {
        current
      }
      image {
        asset {
          _id
          url
        }
        alt
      }
      description
      articleType
      video {
        url
        thumbnail
        caption
      }
      body: _rawBody(resolveReferences: { maxDepth: 10 })
      contentType
      html
      publishedDate
      categories: localizedCategoriesValue {
        _id
        title
        slug {
          current
        }
      }
      author: localizedAuthorValue {
        _id
        image {
          asset {
            _id
            url
          }
          alt
        }
        occupation
        title
        slug {
          current
        }
      }
      relatedArticles: localizedRelatedArticlesValue {
        _id
        language
        _key
        image {
          alt
          asset {
            _id
            url
          }
        }
        title
        categories: localizedCategoriesValue {
          title
        }
        author: localizedAuthorValue {
          title
          slug {
            current
          }
        }
        slug {
          current
        }
      }
      relatedProducts: localizedRelatedProductsValue {
        _key
        ...SanityGlobalProductFields
      }
    }
  }
`;
